import React from "react"
import { Input } from "antd"

/**
 * Application V2 only — preserve in-progress decimals while typing; strip letters.
 * Caps decimals at maxDecimalPlaces during typing (third digit cannot be entered).
 * Optionally allow a leading "-".
 */
export const moneyTypingValue = (value, maxDecimalPlaces = 2, allowNegative = false) => {
    if (value === null || typeof value === "undefined") return ""
    const raw = value.toString().replace(/[$,\s]/g, "")
    if (allowNegative && raw.trim() === "-") return "-"
    const negative = allowNegative && raw.trim().startsWith("-")
    let cleaned = raw.replace(/[^\d.]/g, "")
    const firstDot = cleaned.indexOf(".")
    if (firstDot !== -1) {
        cleaned =
            cleaned.slice(0, firstDot + 1) +
            cleaned.slice(firstDot + 1).replace(/\./g, "")
    }
    if (firstDot === -1) {
        if (!cleaned) return negative ? "-" : ""
        // A leading zero followed by more digits is just a typo: 02122 -> 2122.
        return `${negative ? "-" : ""}${cleaned.replace(/^0+(?=\d)/, "")}`
    }
    const [rawIntPart = "", ...rest] = cleaned.split(".")
    const intPart = rawIntPart.replace(/^0+(?=\d)/, "")
    const decimalRaw = rest.join("") || ""
    const decimal =
        maxDecimalPlaces == null
            ? decimalRaw
            : decimalRaw.slice(0, maxDecimalPlaces)
    if (decimal === "" && raw.replace(/^-/, "").endsWith(".")) {
        return `${negative ? "-" : ""}${intPart || "0"}.`
    }
    if (decimal === "") {
        if (!intPart) return negative ? "-" : ""
        return `${negative ? "-" : ""}${intPart}`
    }
    return `${negative ? "-" : ""}${intPart || "0"}.${decimal}`
}

/**
 * Application V2 only — what the field shows: the typed value with thousands
 * separators in the whole part (10000 -> 10,000), decimals and a trailing
 * "." left as typed. The stored value never carries commas.
 */
export const moneyDisplayValue = (value) => {
    if (value === null || typeof value === "undefined" || value === "") return ""
    const raw = value.toString().replace(/,/g, "")
    const negative = raw.startsWith("-")
    const body = negative ? raw.slice(1) : raw
    const dot = body.indexOf(".")
    const whole = dot === -1 ? body : body.slice(0, dot)
    const rest = dot === -1 ? "" : body.slice(dot)
    const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
    return `${negative ? "-" : ""}${grouped}${rest}`
}

/**
 * Application V2 only — $ money input with 2 decimal places while typing and
 * thousands separators on screen. Does not allow negatives unless min < 0 is
 * passed. onChange receives string | null (null when empty, no commas) for
 * call-site compatibility.
 */
export const DollarInputV2 = (props) => {
    const allowNegative = props.min !== undefined && Number(props.min) < 0
    const handleChange = (e) => {
        const next = moneyTypingValue(e.target.value, 2, allowNegative)
        if (props.onChange) {
            props.onChange(next === "" ? null : next)
        }
    }
    return (
        <Input
            prefix="$"
            suffix={props.suffix}
            style={props.style}
            className={props.className ? props.className : "w-100"}
            defaultValue={props.defaultValue}
            value={moneyDisplayValue(props.value)}
            onChange={handleChange}
            onBlur={props.onBlur}
            onPressEnter={props.onPressEnter}
            size={props.size || "large"}
            placeholder={props.placeholder}
            disabled={props.disabled}
            required={props.required}
            status={props.status}
        />
    )
}
